// ---------------------------------------------------------------------------
// ROLE-BASED SIDEBAR VISIBILITY
//
// Some screens are structurally empty for certain roles, so showing them
// in the sidebar is a dead end — the user clicks through to a blank table
// every time.
//
// The Dashboard permission section is locked ON for the Manager and
// Company Owner presets (see employeeRoles.js), which means these routes
// are force-granted and can't be switched off per-user in the wizard.
// Tech Admin can switch the section off, but when it IS on the same four
// screens are just as empty. This map is where that's corrected.
//
// WHY THESE FOUR, AND NOT THE REST OF THE DASHBOARD SECTION:
// the backend list endpoints for these are scoped to the LOGGED-IN user's
// own shift, not to the location —
//
//     /ticketOut/list     (userId, locationId)
//     /bonus/bonusList    (userId)
//     /raffle/listRaffles (userId)
//     /clock/*            the user's own clock records
//
// Managers, company owners and tech admins all carry ROLE_ADMIN, which
// bypasses clock-in; the app never opens a shift session for them, so
// they have no records of their own and every one of these screens
// renders empty.
//
// Customer Access (/customer/list) and Note (/note/list) are LOCATION-
// scoped, so they show real data for these roles and are deliberately
// not here.
//
// TRANSACTION IS INTENTIONALLY EXCLUDED even though /transaction/list is
// shift-scoped the same way. Kept in the sidebar by explicit request —
// do not "complete the set" by adding it back.
//
// TO CHANGE THIS LATER: edit HIDDEN_ROUTES_BY_ROLE. Route ids come from
// routes/index.js.
// ---------------------------------------------------------------------------

// Screens whose contents belong to one employee's shift.
export const SHIFT_SCOPED_ROUTES = [
  'ticket-out',
  'bonus',
  'raffle',
  'check-in-out',
];

// authority -> route ids to hide from the sidebar.
//
// Keyed on the specific MARKER role of each preset, NOT on ROLE_ADMIN.
// All three of these presets carry ROLE_ADMIN — but so does Reading Guy,
// who DOES clock in and needs these screens, so keying on ROLE_ADMIN
// would hide them far more widely than intended.
//
//   ROLE_MANAGER      the unambiguous manager marker (same reasoning as
//                     BoostEligibility on the server).
//   ROLE_MASTER_ADMIN the company owner — which NDS owner accounts also
//                     carry.
//   ROLE_TECH_ADMIN   NDS remote admin. Same situation as the other two:
//                     ROLE_ADMIN bypasses clock-in, the app never opens a
//                     shift session for them, so these four screens are
//                     always empty.
export const HIDDEN_ROUTES_BY_ROLE = {
  ROLE_MANAGER: SHIFT_SCOPED_ROUTES,
  ROLE_MASTER_ADMIN: SHIFT_SCOPED_ROUTES,
  ROLE_TECH_ADMIN: SHIFT_SCOPED_ROUTES,
};

// ---------------------------------------------------------------------------
// VIEW-ONLY SIDEBAR VISIBILITY
//
// A SEPARATE rule from the role-based map above, and it must stay separate.
//
// These screens are write-only by nature: with every control removed at a
// VIEW-ONLY location there is nothing left on the page, so the menu entry
// leads to a blank screen. But they are NOT always empty — a manager at
// their own full-control location starts reading sessions there as a normal
// part of the job. Hiding them by ROLE would break that; hiding them by
// MODE only affects the locations where the screen really is empty.
//
// Everything else stays visible in view-only: the whole point is that a
// viewer can still see the location.
// ---------------------------------------------------------------------------

export const VIEW_ONLY_HIDDEN_ROUTES = [
  // Start a session / open a previous session / save meter readings —
  // there is no read-only content on this screen at all. Reading Sessions
  // Record and Machine Reading Pictures stay, and that's where a viewer
  // reads the readings.
  'add-machine-reading',
];

/**
 * True when this route should be hidden from the sidebar.
 *
 * Two independent reasons, and they have different scope:
 *
 *   ROLE  (HIDDEN_ROUTES_BY_ROLE) — applies at EVERY location, including
 *         the user's own. The screen is empty for that role no matter
 *         where they are, because its data is scoped to their own shift.
 *
 *   MODE  (VIEW_ONLY_HIDDEN_ROUTES) — applies ONLY at a view-only
 *         location. The screen is write-only, so with the controls gone
 *         there is nothing left; at a full-control location the same user
 *         needs it and keeps it.
 *
 * Either way this hides the MENU ENTRY only — the route itself still
 * resolves, so a bookmarked URL keeps working and nobody is hard-locked
 * out of something they genuinely need.
 */
export function isRouteHiddenForUser(routeId, authorities, viewOnly) {
  if (!routeId) return false;

  // Mode-based rule (view-only locations only).
  if (viewOnly && VIEW_ONLY_HIDDEN_ROUTES.includes(routeId)) return true;

  // Role-based rule (applies everywhere, including the home location).
  if (!Array.isArray(authorities)) return false;
  return authorities.some((authority) => {
    const hidden = HIDDEN_ROUTES_BY_ROLE[authority];
    return !!hidden && hidden.includes(routeId);
  });
}
